import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, ShoppingBag, Handshake, Users, LogOut,
  Plus, Pencil, Trash2, Download, X, DollarSign, Ticket, Mail, Tag, ClipboardList, Clapperboard, Store, KeyRound, Building2, CreditCard, PenLine, Tags,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MediaManager } from "@/components/MediaManager";
import { mediaSrc } from "@/components/HeroVideo";
import { ProposalBuilder, AccountPanel, TeamPanel } from "@/components/AdminTools";
import { CompanyPanel } from "@/components/CompanyPanel";
import { PaymentsPanel } from "@/components/PaymentsPanel";
import { ContentPanel } from "@/components/ContentPanel";
import { ServicesPanel } from "@/components/ServicesPanel";

const inputCls = "w-full rounded-lg border border-neutral-700 bg-black px-3 py-2 text-sm text-stone-100 outline-none focus:border-cyan-500";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [subs, setSubs] = useState([]);
  const [promos, setPromos] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [proposalFor, setProposalFor] = useState(null);
  const [editing, setEditing] = useState(null); // event object or "new"

  const refresh = useCallback(async () => {
    try {
      const [s, e, o, sp, su, pr, bk, vn, dp, rq] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/events"), api.get("/admin/orders"),
        api.get("/admin/sponsors"), api.get("/admin/subscribers"), api.get("/admin/promos"), api.get("/admin/bookings"),
        api.get("/admin/vendors"), api.get("/admin/sponsor-deposits"), api.get("/admin/requests"),
      ]);
      setStats(s.data); setEvents(e.data); setOrders(o.data); setSponsors(sp.data); setSubs(su.data); setPromos(pr.data); setBookings(bk.data);
      setVendors(vn.data); setDeposits(dp.data); setRequests(rq.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/admin/login"); return; }
    refresh();
  }, [user, loading, navigate, refresh]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/attendees/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "attendees.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Attendee CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const delEvent = async (id) => {
    if (!window.confirm("Delete this event and its tickets?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Event deleted.");
    refresh();
  };

  const setSponsorStatus = async (id, status) => {
    await api.put(`/admin/sponsors/${id}`, { status });
    refresh();
  };

  const setBookingStatus = async (id, status) => {
    await api.put(`/admin/bookings/${id}`, { status });
    refresh();
  };

  if (loading || !user) return <div className="px-4 py-32 text-center text-neutral-500">Loading…</div>;

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "events", label: "Events", icon: CalendarDays },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "sponsors", label: "Sponsors", icon: Handshake },
    { id: "vendors", label: "Vendors", icon: Store },
    { id: "requests", label: "Requests", icon: ClipboardList },
    { id: "bookings", label: "Bookings", icon: ClipboardList },
    { id: "promos", label: "Promos", icon: Tag },
    { id: "media", label: "Media", icon: Clapperboard },
    { id: "subscribers", label: "List", icon: Users },
    { id: "content", label: "Website Text", icon: PenLine },
    { id: "services", label: "Services", icon: Tags },
    { id: "company", label: "Company", icon: Building2 },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "account", label: "Account", icon: KeyRound },
  ];

  return (
    <div className="grain min-h-screen">
      <header className="sticky top-0 z-40 border-b border-neutral-800 bg-[rgba(15,14,13,0.9)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div>
            <h1 className="font-display text-2xl font-black uppercase text-cyan-50">Organizer Admin</h1>
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-cyan-500">EON Infinity Entertainment</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="hidden font-display font-bold uppercase text-neutral-400 hover:text-cyan-400 sm:block">View Site</a>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-neutral-700 px-4 py-2 font-display font-bold uppercase text-neutral-300 hover:border-cyan-500 hover:text-cyan-400">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-display font-bold uppercase transition-colors ${tab === t.id ? "bg-cyan-500 text-black" : "border border-neutral-700 text-neutral-300 hover:border-cyan-500"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && stats && (
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { icon: DollarSign, l: "Gross Sales", v: `$${stats.gross.toLocaleString()}` },
              { icon: Ticket, l: "Tickets Sold", v: stats.tickets_sold },
              { icon: ShoppingBag, l: "Paid Orders", v: stats.orders },
              { icon: Handshake, l: "Sponsor Inquiries", v: stats.sponsor_inquiries },
              { icon: Mail, l: "Subscribers", v: stats.subscribers },
              { icon: CalendarDays, l: "Events", v: stats.events },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-5" data-testid={`stat-${i}`}>
                <s.icon className="h-5 w-5 text-cyan-500" />
                <div className="mt-3 font-display text-3xl font-black text-cyan-50">{s.v}</div>
                <div className="font-mono-label text-[10px] uppercase tracking-wider text-neutral-500">{s.l}</div>
              </div>
            ))}
          </div>
        )}

        {/* EVENTS */}
        {tab === "events" && (
          <div className="mt-6">
            <button onClick={() => setEditing("new")} data-testid="admin-new-event-btn" className="mb-4 flex items-center gap-2 rounded-full bg-cyan-500 px-5 py-2.5 font-display font-black uppercase text-black hover:scale-105">
              <Plus className="h-4 w-4" /> New Event
            </button>
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4" data-testid={`admin-event-row-${e.id}`}>
                  <div className="flex items-center gap-4">
                    {e.hero_image ? (
                      <img src={e.hero_image} alt="" className="h-14 w-20 rounded-lg object-cover" />
                    ) : (
                      <div className="flex h-14 w-20 items-center justify-center rounded-lg bg-stone-800 font-display text-xs font-black uppercase text-cyan-500/40">EON</div>
                    )}
                    <div>
                      <div className="font-display text-lg font-bold uppercase text-stone-100">{e.title}</div>
                      <div className="text-xs text-neutral-500">{new Date(e.date).toLocaleDateString()} · {e.city}, {e.state} · {e.tiers?.length || 0} tiers · {e.published ? "Published" : "Draft"}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(e)} data-testid={`admin-edit-event-${e.id}`} className="rounded-lg border border-neutral-700 p-2 text-neutral-300 hover:border-cyan-500 hover:text-cyan-400"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delEvent(e.id)} data-testid={`admin-delete-event-${e.id}`} className="rounded-lg border border-neutral-700 p-2 text-red-400 hover:border-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ORDERS */}
        {tab === "orders" && (
          <div className="mt-6">
            <button onClick={exportCsv} data-testid="admin-export-attendees-btn" className="mb-4 flex items-center gap-2 rounded-full border border-cyan-500 px-5 py-2.5 font-display font-bold uppercase text-cyan-400 hover:bg-cyan-500 hover:text-black">
              <Download className="h-4 w-4" /> Export Attendees CSV
            </button>
            <div className="overflow-x-auto rounded-xl border border-neutral-800">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#111111] font-mono-label text-[10px] uppercase tracking-wider text-neutral-400">
                  <tr>{["Ref", "Name", "Email", "Event", "Tier", "Qty", "Amount", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-neutral-800 text-stone-200" data-testid={`admin-order-${o.id}`}>
                      <td className="px-4 py-3 font-mono-label text-cyan-400">{o.reference_code}</td>
                      <td className="px-4 py-3">{o.buyer_name}</td>
                      <td className="px-4 py-3 text-neutral-400">{o.buyer_email}</td>
                      <td className="px-4 py-3">{o.event_title}</td>
                      <td className="px-4 py-3">{o.tier_name}</td>
                      <td className="px-4 py-3">{o.quantity}</td>
                      <td className="px-4 py-3">${Number(o.amount).toFixed(2)}</td>
                      <td className="px-4 py-3"><span className={o.payment_status === "paid" ? "text-green-400" : "text-yellow-500"}>{o.payment_status}</span></td>
                    </tr>
                  ))}
                  {!orders.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-500">No orders yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SPONSORS */}
        {tab === "sponsors" && (
          <div className="mt-6 space-y-3">
            {deposits.length > 0 && (
              <div className="rounded-2xl border border-cyan-500/30 bg-[#0a0a0a] p-4" data-testid="admin-deposits">
                <h3 className="font-display text-lg uppercase text-gold">Deposits Paid · ${deposits.filter((d) => d.payment_status === "paid").reduce((a, d) => a + d.amount, 0).toLocaleString()}</h3>
                <div className="mt-3 space-y-2">
                  {deposits.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-800 bg-black/30 px-4 py-3 text-sm" data-testid={`admin-deposit-${d.id}`}>
                      <div><span className="font-bold text-stone-100">{d.company}</span> <span className="text-neutral-500">· {d.tier_name} · {d.name} · {d.email}</span></div>
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full px-2 py-0.5 font-mono-label text-[10px] uppercase ${d.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-stone-700 text-neutral-300"}`}>{d.payment_status} ${d.amount.toLocaleString()}</span>
                        {d.deck_url && <a href={mediaSrc(d.deck_url)} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline" data-testid={`deck-link-${d.id}`}>Deck PDF</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sponsors.map((s) => (
              <div key={s.id} className="rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4" data-testid={`admin-sponsor-${s.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-display text-lg font-bold uppercase text-stone-100">{s.name} {s.company && <span className="text-neutral-500">· {s.company}</span>}</div>
                  <select value={s.status} onChange={(e) => setSponsorStatus(s.id, e.target.value)} data-testid={`sponsor-status-${s.id}`} className="rounded-lg border border-neutral-700 bg-black px-3 py-1.5 text-xs text-stone-100">
                    {["new", "contacted", "closed"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div className="mt-1 text-sm text-neutral-400">{s.email} {s.phone && `· ${s.phone}`} {(s.tier || s.package) && `· ${s.tier || s.package}`} {s.budget && `· ${s.budget}`} {s.target_event && `· ${s.target_event}`}</div>
                {s.message && <p className="mt-2 text-sm text-neutral-300">{s.message}</p>}
              </div>
            ))}
            {!sponsors.length && <p className="text-neutral-500">No sponsor inquiries yet.</p>}
          </div>
        )}

            {/* VENDORS TAB */}
        {tab === "vendors" && (
          <div className="mt-6" data-testid="admin-vendors">
            <div className="grid gap-3 sm:grid-cols-3">
              {events.map((e) => (
                <div key={e.id} className="rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4">
                  <div className="font-display text-sm uppercase text-stone-200">{e.title}</div>
                  <div className="mt-2 space-y-1">
                    {(e.booth_types || []).map((b) => {
                      const sold = vendors.filter((v) => v.event_id === e.id && v.booth_type_id === b.id && v.payment_status === "paid").length;
                      return <div key={b.id} className="flex justify-between text-xs text-neutral-400"><span>{b.name}</span><span className={sold >= b.quantity ? "text-red-400" : "text-cyan-400"}>{sold}/{b.quantity}</span></div>;
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2">
              {vendors.map((v) => (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-800 bg-[#0a0a0a] px-4 py-3 text-sm" data-testid={`admin-vendor-${v.id}`}>
                  <div><span className="font-bold text-stone-100">{v.business_name}</span> <span className="text-neutral-500">· {v.booth_name} · {v.event_title} · {v.contact_name} · {v.email}{v.vendor_category && ` · ${v.vendor_category}`}</span></div>
                  <span className={`rounded-full px-2 py-0.5 font-mono-label text-[10px] uppercase ${v.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-stone-700 text-neutral-300"}`}>{v.payment_status} ${v.amount.toLocaleString()} · {v.reference_code}</span>
                </div>
              ))}
              {!vendors.length && <p className="text-neutral-500">No vendor applications yet.</p>}
            </div>
          </div>
        )}

        {/* REQUESTS */}
        {tab === "requests" && (
          <div className="mt-6" data-testid="admin-requests">
            <div className="grid gap-3 sm:grid-cols-3">
              {[["New", requests.filter((r) => r.status === "new").length], ["Quoted", requests.filter((r) => r.status === "quoted").length],
                ["Pipeline", `$${requests.filter((r) => r.status === "quoted").reduce((a, r) => a + (Number(r.quote_amount) || 0), 0).toLocaleString()}`]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4"><div className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-500">{l}</div><div className="mt-1 font-display text-2xl text-gold">{v}</div></div>
              ))}
            </div>
            <div className="mt-6 space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4" data-testid={`admin-request-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-mono-label text-[10px] uppercase tracking-widest text-cyan-500">{r.reference} · {r.lane_name}</span>
                      <div className="font-bold text-stone-100">{r.name}{r.company && ` · ${r.company}`}</div>
                      <div className="text-sm text-neutral-400">{r.email}{r.phone && ` · ${r.phone}`}{r.event_date && ` · ${r.event_date}`}{r.city && ` · ${r.city}`}{r.guests && ` · ${r.guests} guests`}{r.budget && ` · ${r.budget}`}{r.artist && ` · ${r.artist}`}</div>
                      {r.details && <p className="mt-1 text-sm text-neutral-300">{r.details}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" placeholder="Quote $" defaultValue={r.quote_amount || ""} data-testid={`request-quote-${r.id}`}
                        onBlur={(e) => api.put(`/admin/requests/${r.id}`, { quote_amount: Number(e.target.value) || 0 }).then(refresh)} className={`${inputCls} w-28`} />
                      <select value={r.status} onChange={(e) => api.put(`/admin/requests/${r.id}`, { status: e.target.value }).then(refresh)} data-testid={`request-status-${r.id}`} className={inputCls}>
                        {["new", "contacted", "quoted", "won", "lost"].map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                      <button onClick={() => setProposalFor(proposalFor === r.id ? null : r.id)} data-testid={`request-proposal-btn-${r.id}`} className="btn-chrome px-3 py-2 text-[10px]">{r.proposal_file ? "Edit proposal" : "Build proposal"}</button>
                    </div>
                  </div>
                  {proposalFor === r.id && <ProposalBuilder req={r} onDone={() => { setProposalFor(null); refresh(); }} />}
                </div>
              ))}
              {!requests.length && <p className="text-neutral-500">No requests yet.</p>}
            </div>
          </div>
        )}

        {/* BOOKINGS */}
        {tab === "bookings" && (
          <div className="mt-6 space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4" data-testid={`admin-booking-${b.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-cond text-lg font-bold uppercase text-stone-100">{b.name} {b.company && <span className="text-neutral-500">· {b.company}</span>}</div>
                  <select value={b.status} onChange={(e) => setBookingStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className="rounded-lg border border-neutral-700 bg-black px-3 py-1.5 text-xs text-stone-100">
                    {["new", "contacted", "closed"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div className="mt-1 text-sm text-neutral-400">{b.email}{b.phone && ` · ${b.phone}`}{b.service && ` · ${b.service}`}{b.budget && ` · ${b.budget}`}{b.event_date && ` · ${b.event_date}`}</div>
                {b.message && <p className="mt-2 text-sm text-neutral-300">{b.message}</p>}
              </div>
            ))}
            {!bookings.length && <p className="text-neutral-500">No service / booking requests yet.</p>}
          </div>
        )}

        {/* PROMOS */}
        {tab === "promos" && <PromoManager promos={promos} events={events} onChange={refresh} />}
        {tab === "media" && <MediaManager />}
        {tab === "account" && <><AccountPanel user={user} /><TeamPanel /></>}
        {tab === "company" && <CompanyPanel />}
        {tab === "payments" && <PaymentsPanel />}
        {tab === "content" && <ContentPanel />}
        {tab === "services" && <ServicesPanel />}
        {/* SUBSCRIBERS */}
        {tab === "subscribers" && (
          <div className="mt-6 rounded-xl border border-neutral-800 bg-[#0a0a0a] p-5">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
              {subs.map((s) => <div key={s.id} className="rounded-lg bg-black/30 px-3 py-2 text-sm text-neutral-300">{s.email}</div>)}
            </div>
            {!subs.length && <p className="text-neutral-500">No subscribers yet.</p>}
          </div>
        )}
      </div>

      {editing && <EventFormModal event={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function PromoManager({ promos, events, onChange }) {
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: 10, max_uses: 0, expires_at: "", event_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    if (!form.code.trim()) return toast.error("Enter a code.");
    setBusy(true);
    try {
      await api.post("/admin/promos", {
        code: form.code, discount_type: form.discount_type, value: Number(form.value),
        max_uses: Number(form.max_uses) || 0, active: true,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        event_id: form.event_id || "",
      });
      toast.success("Promo code created.");
      setForm({ code: "", discount_type: "percent", value: 10, max_uses: 0, expires_at: "", event_id: "" });
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    await api.put(`/admin/promos/${p.id}`, { ...p, active: !p.active });
    onChange();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    await api.delete(`/admin/promos/${id}`);
    toast.success("Promo deleted.");
    onChange();
  };

  return (
    <div className="mt-6">
      <div className="grid grid-cols-2 gap-2 rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-4 sm:grid-cols-6" data-testid="promo-create-form">
        <input placeholder="CODE" value={form.code} onChange={set("code")} data-testid="promo-code-field" className={`${inputCls} uppercase`} />
        <select value={form.discount_type} onChange={set("discount_type")} data-testid="promo-type-field" className={inputCls}>
          <option value="percent">% off</option>
          <option value="amount">$ off</option>
        </select>
        <input type="number" placeholder="Value" value={form.value} onChange={set("value")} data-testid="promo-value-field" className={inputCls} />
        <input type="number" placeholder="Max uses (0=∞)" value={form.max_uses} onChange={set("max_uses")} className={inputCls} />
        <select value={form.event_id} onChange={set("event_id")} className={inputCls}>
          <option value="">All events</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.title.slice(0, 22)}</option>)}
        </select>
        <button onClick={create} disabled={busy} data-testid="promo-create-btn" className="rounded-lg bg-cyan-500 font-display font-black uppercase text-black hover:scale-[1.02] disabled:opacity-60">
          Add
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {promos.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4" data-testid={`promo-row-${p.id}`}>
            <div className="flex items-center gap-4">
              <span className="font-display text-xl font-black uppercase tracking-wider text-cyan-400">{p.code}</span>
              <span className="font-mono-label text-xs uppercase text-neutral-400">
                {p.discount_type === "percent" ? `${p.value}% off` : `$${p.value} off`}
                {" · "}{p.used_count}{p.max_uses ? `/${p.max_uses}` : ""} used
                {p.event_id ? " · event-only" : " · all events"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.id}`} className={`rounded-full px-3 py-1.5 font-mono-label text-[10px] uppercase ${p.active ? "bg-green-500/20 text-green-400" : "bg-stone-700 text-neutral-400"}`}>
                {p.active ? "Active" : "Paused"}
              </button>
              <button onClick={() => del(p.id)} data-testid={`promo-delete-${p.id}`} className="rounded-lg border border-neutral-700 p-2 text-red-400 hover:border-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {!promos.length && <p className="text-neutral-500">No promo codes yet — create one above to drive early-bird sales.</p>}
      </div>
    </div>
  );
}

function EventFormModal({ event, onClose, onSaved }) {
  const blank = {
    title: "", subtitle: "", description: "", date: "", location_name: "", address: "",
    city: "", state: "", map_query: "", hero_image: "", video_url: "", published: true, featured: false, category: "trail-rides",
    gallery: [], lineup: [], vendors: [],
  };
  const [form, setForm] = useState(() => {
    if (!event) return blank;
    return {
      ...blank, ...event,
      date: event.date ? new Date(event.date).toISOString().slice(0, 16) : "",
    };
  });
  const [tiers, setTiers] = useState(event?.tiers || []);
  const [booths, setBooths] = useState(event?.booth_types || []);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.date) return toast.error("Title and date are required.");
    setBusy(true);
    try {
      const payload = { ...form, date: new Date(form.date).toISOString(),
        booth_types: booths.filter((b) => b.name).map((b) => ({ id: b.id || "", name: b.name, description: b.description || "", price: Number(b.price) || 0, quantity: Number(b.quantity) || 0 })) };
      let saved;
      if (event) saved = (await api.put(`/admin/events/${event.id}`, payload)).data;
      else saved = (await api.post("/admin/events", payload)).data;
      toast.success("Event saved.");
      // Save new tiers (ones without id)
      for (const t of tiers) {
        if (!t.id) {
          await api.post(`/admin/events/${saved.id}/tiers`, {
            name: t.name, description: t.description || "", price: Number(t.price),
            capacity: Number(t.capacity) || 100, sort_order: Number(t.sort_order) || 0, active: true,
          });
        }
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addTier = () => setTiers((t) => [...t, { name: "", description: "", price: 0, capacity: 100, sort_order: t.length }]);
  const updTier = (i, k, v) => setTiers((ts) => ts.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const delTier = async (i) => {
    const t = tiers[i];
    if (t.id) { await api.delete(`/admin/tiers/${t.id}`); toast.success("Tier removed."); }
    setTiers((ts) => ts.filter((_, idx) => idx !== i));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm" data-testid="event-form-modal">
      <div className="my-8 w-full max-w-2xl rounded-3xl border border-cyan-500/20 bg-[#0a0a0a] p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-black uppercase text-cyan-50">{event ? "Edit Event" : "New Event"}</h2>
          <button onClick={onClose} data-testid="modal-close-btn" className="rounded-lg p-2 text-neutral-400 hover:text-cyan-400"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input placeholder="Event title" value={form.title} onChange={set("title")} data-testid="event-title-input" className={`${inputCls} sm:col-span-2`} />
          <input placeholder="Subtitle / tagline" value={form.subtitle} onChange={set("subtitle")} className={`${inputCls} sm:col-span-2`} />
          <textarea placeholder="Description" value={form.description} onChange={set("description")} rows={3} className={`${inputCls} sm:col-span-2`} />
          <label className="font-mono-label text-[10px] uppercase text-neutral-500 sm:col-span-2">Date & Time
            <input type="datetime-local" value={form.date} onChange={set("date")} data-testid="event-date-input" className={`${inputCls} mt-1`} />
          </label>
          <input placeholder="Venue name" value={form.location_name} onChange={set("location_name")} className={inputCls} />
          <input placeholder="Address" value={form.address} onChange={set("address")} className={inputCls} />
          <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} />
          <input placeholder="State" value={form.state} onChange={set("state")} className={inputCls} />
          <select value={form.category} onChange={set("category")} data-testid="event-category-select" className={`${inputCls} sm:col-span-2`}>
            {[["trail-rides", "Trail Rides & Equestrian"], ["concerts", "Concerts & Festivals"], ["corporate", "Corporate & Private Events"], ["sports", "Sports & Arena"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Hero image URL" value={form.hero_image} onChange={set("hero_image")} className={`${inputCls} sm:col-span-2`} />
          <input placeholder="Video embed URL (YouTube/Vimeo)" value={form.video_url} onChange={set("video_url")} className={`${inputCls} sm:col-span-2`} />
          <div className="flex gap-4 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="event-published-checkbox" /> Published</label>
            <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="event-featured-checkbox" /> Featured (Next Up)</label>
          </div>
        </div>

        {/* Tiers */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold uppercase text-cyan-400">Ticket Tiers</h3>
            <button onClick={addTier} data-testid="add-tier-btn" className="flex items-center gap-1 rounded-full border border-cyan-500 px-3 py-1.5 text-xs font-bold uppercase text-cyan-400 hover:bg-cyan-500 hover:text-black"><Plus className="h-3.5 w-3.5" /> Add Tier</button>
          </div>
          <div className="mt-3 space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`tier-row-${i}`}>
                <input placeholder="Name" value={t.name} onChange={(e) => updTier(i, "name", e.target.value)} className={`${inputCls} col-span-4`} disabled={!!t.id} />
                <input type="number" placeholder="Price" value={t.price} onChange={(e) => updTier(i, "price", e.target.value)} className={`${inputCls} col-span-3`} disabled={!!t.id} />
                <input type="number" placeholder="Cap" value={t.capacity} onChange={(e) => updTier(i, "capacity", e.target.value)} className={`${inputCls} col-span-3`} disabled={!!t.id} />
                <button onClick={() => delTier(i)} className="col-span-2 rounded-lg border border-neutral-700 text-red-400 hover:border-red-500"><Trash2 className="mx-auto h-4 w-4" /></button>
              </div>
            ))}
            {tiers.some((t) => t.id) && <p className="font-mono-label text-[10px] uppercase text-stone-600">Existing tiers are locked; delete & re-add to change price.</p>}
          </div>
        </div>

        {/* Booths */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold uppercase text-cyan-400">Vendor Booths</h3>
            <button onClick={() => setBooths((b) => [...b, { id: "", name: "", description: "", price: 350, quantity: 10 }])} data-testid="add-booth-btn" className="flex items-center gap-1 rounded-full border border-cyan-500 px-3 py-1.5 text-xs font-bold uppercase text-cyan-400 hover:bg-cyan-500 hover:text-black"><Plus className="h-3.5 w-3.5" /> Add Booth Type</button>
          </div>
          <div className="mt-3 space-y-2">
            {booths.map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`booth-row-${i}`}>
                <input placeholder="Booth name" value={b.name} onChange={(e) => setBooths((bs) => bs.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} className={`${inputCls} col-span-4`} />
                <input type="number" placeholder="Price" value={b.price} onChange={(e) => setBooths((bs) => bs.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x))} className={`${inputCls} col-span-3`} />
                <input type="number" placeholder="Qty" value={b.quantity} onChange={(e) => setBooths((bs) => bs.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} className={`${inputCls} col-span-3`} />
                <button onClick={() => setBooths((bs) => bs.filter((_, idx) => idx !== i))} className="col-span-2 rounded-lg border border-neutral-700 text-red-400 hover:border-red-500"><Trash2 className="mx-auto h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="save-event-btn" className="btn-gold mt-6 w-full rounded-full py-3.5 font-display text-lg font-black uppercase disabled:opacity-60">
          {busy ? "Saving…" : "Save Event"}
        </button>
      </div>
    </div>
  );
}
